import React from 'react';
import { useCallback, useRef } from 'react';
import PasteInstrumentModal from '@/components/wizard/PasteInstrumentModal';
import { useModal } from '@/hooks/use-modal';
import { PastePreviewModal } from './PastePreviewModal';
import type { ClipboardSource } from './test-table-editor/parseClipboardTable';
import type { InstrumentIdentity } from '@/components/wizard/InstrumentIdentityFields';

/**
 * Shared hook that gates paste operations behind a preview modal.
 *
 * On every paste of tabular data the user sees what is about to be inserted and
 * confirms it ("Colar") or cancels. Which dialog does the asking depends on
 * what the paste creates: a paste on the canvas turns into a whole instrument
 * and goes through the same dialog the "Colar uma tabela pronta" menu item
 * opens, fields and all; a paste inside an existing table only fills cells, and
 * there is no instrument being born for those fields to describe.
 */
export const usePastePreview = () => {
    const { openModal, closeModal } = useModal();

    // Store modal functions in a ref so the returned callbacks never change identity
    const modalRef = useRef({ openModal, closeModal });
    modalRef.current = { openModal, closeModal };

    /**
     * Opens the cell-level preview. `applyFn` is called only when the user
     * clicks "Colar".
     */
    const showPreviewOrApply = useCallback(
        (parsedData: string[][], source: ClipboardSource, applyFn: () => void) => {
            const { openModal: open, closeModal: close } = modalRef.current;

            open({
                title: 'Pré-visualização da colagem',
                content: React.createElement(PastePreviewModal, {
                    parsedData,
                    source,
                    onConfirm: () => {
                        close();
                        applyFn();
                    },
                    onCancel: () => {
                        close();
                    },
                }),
                size: 'lg',
                variant: 'info',
            });
        },
        [], // Stable — no dependencies; reads everything from refs
    );

    /**
     * Opens the instrument dialog on a paste that already landed, and hands
     * back the grid together with the name and the citation the user typed.
     *
     * No title or footer here on purpose: `PasteInstrumentModal` brings its own
     * `AppDialogLayout`, so the dialog the canvas opens is the same one the
     * menu opens rather than a look-alike.
     */
    const showInstrumentPaste = useCallback(
        (
            grid: string[][],
            source: ClipboardSource,
            applyFn: (grid: string[][], identity: InstrumentIdentity) => void,
            /** Lets the dialog ask the API what this table is. Id only. */
            options: { patientId?: string } = {},
        ) => {
            const { openModal: open, closeModal: close } = modalRef.current;

            open({
                content: React.createElement(PasteInstrumentModal, {
                    initialPaste: { grid, source },
                    onConfirm: applyFn,
                    onClose: close,
                    patientId: options.patientId,
                }),
                size: 'lg',
            });
        },
        [],
    );

    return { showPreviewOrApply, showInstrumentPaste };
};
